import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cross, GraduationCap, HeartHandshake, Globe, Landmark } from "lucide-react";
import api from "../lib/api";
import { Reveal, FadeIn, SectionHeading } from "../components/Motion";
import { Button } from "../components/ui/button";

const HERITAGE_IMG = "https://images.pexels.com/photos/24018876/pexels-photo-24018876.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PILLARS = [
  {
    icon: GraduationCap,
    title: "Certified Teacher Education",
    text: "NTT, NST, DNTT and PTT programs aligned with national teacher-education guidelines and modern classroom practice.",
  },
  {
    icon: Globe,
    title: "Language & Global Readiness",
    text: "Spoken English, grammar mastery and IELTS coaching that carry students from Mandya to the world.",
  },
  {
    icon: HeartHandshake,
    title: "Vocational Empowerment",
    text: "Community programs that build sustainable livelihoods for women and rural youth across the region.",
  },
  {
    icon: Landmark,
    title: "Ethical Stewardship",
    text: "A registered Christian charitable trust — transparent, service-minded and accountable to the community we serve.",
  },
];

export default function About() {
  const [founderImg, setFounderImg] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setFounderImg(r.data.founder_image || "")).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="pt-32 lg:pt-40">
      {/* Story */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="about-story-section">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeIn>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Our Story</p>
            </FadeIn>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-forest-deep sm:text-5xl lg:text-6xl">
              <Reveal y={24}>From Mandya's roots,</Reveal>
              <Reveal y={24} delay={0.1}>
                <span className="italic text-terracotta">a legacy of lifting lives.</span>
              </Reveal>
            </h1>
            <Reveal delay={0.2}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
                St. Mary's Charitable Trust is a Christian organization dedicated to transforming lives through quality
                education and vocational training. From our campus beside the old post office on Guthalu Road, we have
                formed teachers, coached dreamers and equipped communities with skills for sustainable livelihoods —
                guided always by values of service and compassion.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Button asChild className="rounded-full bg-forest px-7 font-display text-cream hover:bg-forest-deep" data-testid="about-courses-button">
                  <Link to="/courses">See Our Courses</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-forest/30 px-7 font-display text-forest hover:bg-forest hover:text-cream" data-testid="about-contact-button">
                  <Link to="/contact">Visit the Campus</Link>
                </Button>
              </div>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border-[10px] border-white shadow-2xl shadow-forest/15">
                <img src={HERITAGE_IMG} alt="Heritage facade reflecting the trust's Christian foundation" className="h-[440px] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 left-6 rounded-2xl bg-forest px-6 py-4 shadow-xl" data-testid="about-verse-badge">
                <p className="font-serif text-lg italic text-gold">"Let all that you do be done in love."</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">1 Corinthians 16:14</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Founder */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="founder-section">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-4 rounded-[2.5rem] border border-gold/40" />
              {founderImg ? (
                <img
                  src={founderImg}
                  alt="Joy Magdaleena — Founder & Educational Counselor"
                  className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-xl"
                  data-testid="founder-photo"
                />
              ) : (
                <div
                  className="grain-overlay relative flex aspect-[4/5] w-full flex-col items-center justify-center rounded-[2rem] bg-forest-deep shadow-xl"
                  data-testid="founder-photo"
                >
                  <Cross className="h-7 w-7 text-gold/70" />
                  <span className="mt-6 font-serif text-8xl font-bold text-gold">JM</span>
                  <span className="mt-4 font-serif text-2xl italic text-cream">Joy Magdaleena</span>
                  <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/60">Founder & Counselor</span>
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Leadership</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold text-forest-deep sm:text-4xl">Joy Magdaleena</h2>
            <p className="mt-1 font-display text-sm font-medium uppercase tracking-[0.14em] text-terracotta-dark">
              Founder & Educational Counselor
            </p>
            <blockquote className="mt-7 border-l-2 border-terracotta pl-6 font-serif text-2xl italic leading-relaxed text-forest-deep/90">
              "Every student who walks through our door carries a God-given potential. Our work is simply to help them
              see it — and then equip them to live it."
            </blockquote>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-stone-600">
              Under her counsel, the trust has grown from a single classroom into a centre for teacher education,
              language training and community empowerment — with personal mentorship at the heart of every batch.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-linen/70 py-24 lg:py-32" data-testid="about-pillars-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker="What We Stand For" title="The pillars of our excellence." />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div
                  data-testid={`about-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex h-full gap-6 rounded-3xl border border-forest/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-terracotta/30 hover:shadow-xl hover:shadow-forest/5"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-linen text-terracotta transition-colors duration-500 group-hover:bg-terracotta group-hover:text-white">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-forest-deep">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Community strip */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="community-section">
        <Reveal>
          <div className="grid grid-cols-1 items-center gap-10 rounded-[2.5rem] bg-forest-deep p-10 text-cream sm:p-14 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Cross className="h-8 w-8 text-gold" />
              <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight sm:text-4xl">
                Community & women empowerment, woven into everything we do.
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-cream/70">
                Fee concessions for rural and underprivileged candidates, weekend batches for working women, and
                vocational programs that turn skills into income — education here is a ministry of opportunity.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button asChild size="lg" className="rounded-full bg-gold px-8 font-display text-forest-deep hover:bg-cream" data-testid="community-enquire-button">
                <Link to="/contact">Talk to Us</Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
